import React from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import useDossierDocuments, {
    STATUS_REFUSED,
    STATUS_VALID,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocuments';
import { useDocumentPreviewContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers/DocumentPreviewContext';
import { useDossierDocumentsFiles } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { Layout, LAYOUT_DOCUMENTS_FULL } from '../../../../constants/layouts';
import DossierDocumentsValidationDocumentName from '../DossierDocumentsValidationDocumentName/DossierDocumentsValidationDocumentName';
import DossierDocumentsValidationDocumentStatus from '../DossierDocumentsValidationDocumentStatus/DossierDocumentsValidationDocumentStatus';

export type DossierDocumentsValidationHeaderProps = {
    currentLayout: Layout;
    onLayoutChange: (layout: Layout) => void;
    onDocumentTypeChange: (fichierId: number, typeDocId: number) => void;
    onDocumentStatusChange: (fichierId: number, statusId: number) => void;
    onDocumentDeliassage: (fichierId: number) => void;
    navigationBtn?: React.JSX.Element;
};

const DossierDocumentsValidationHeader: React.FunctionComponent<DossierDocumentsValidationHeaderProps> =
    function (props) {
        const {
            currentLayout,
            onLayoutChange,
            onDocumentTypeChange,
            onDocumentStatusChange,
            onDocumentDeliassage,
            navigationBtn,
        } = props;
        const { getDocumentTypes } = useDossierDocumentsFiles();
        const { getStatusIdentifier, getColorByStatus, getFileIcon } = useDossierDocuments();
        const handleLayoutChange = () => {
            onLayoutChange(LAYOUT_DOCUMENTS_FULL);
        };
        const { documentPreview, setDocumentPreview } = useDocumentPreviewContext();
        const { palette } = useTheme();
        const documentColor = documentPreview?.statusId
            ? getColorByStatus(getStatusIdentifier(documentPreview?.statusId))
            : 'primary';
        const documentIcon = documentPreview?.fileType
            ? getFileIcon(documentPreview?.fileType)
            : 'file';

        const handleAccept = () => {
            if (!documentPreview?.fichierId) {
                return;
            }

            onDocumentStatusChange(documentPreview.fichierId as number, STATUS_VALID);
            setDocumentPreview({
                ...documentPreview,
                statusId: STATUS_VALID,
            });
        };

        const handleDeny = () => {
            if (!documentPreview?.fichierId) {
                return;
            }

            onDocumentStatusChange(documentPreview.fichierId as number, STATUS_REFUSED);
            setDocumentPreview({
                ...documentPreview,
                statusId: STATUS_REFUSED,
            });
        };

        const headerSx = {
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            padding: '15px 16px',
            borderBottomColor: palette.grey[200],
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
        };

        const statusWrapperSx = {
            display: 'flex',
            gap: '0 14px',
            alignItems: 'center',
            marginLeft: 'auto',
            marginRight: '0',
        };

        const iconSx = {
            lineHeight: 0,
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            transition: 'color 0.25s ease',
        };

        const changeLayoutIconSx = {
            ...iconSx,
            color: palette.secondary.main,
            ':hover': {
                color: palette.primary.main,
            },
        };

        return (
            <Box sx={headerSx}>
                {navigationBtn}
                {documentPreview && (
                    <DossierDocumentsValidationDocumentName
                        fileIcon={documentIcon}
                        documentColor={documentColor}
                        label={documentPreview.documentLabel as string}
                        fileName={documentPreview.fileName as string}
                        documentTypes={getDocumentTypes(
                            documentPreview.dossierId as number,
                            documentPreview.expediteurType,
                            documentPreview.documentTypeId,
                        )}
                        currentTypeId={documentPreview.documentTypeId as number}
                        onDocumentTypeChange={(newDocType) => {
                            onDocumentTypeChange(
                                documentPreview.fichierId as number,
                                newDocType.id,
                            );
                            setDocumentPreview({
                                ...documentPreview,
                                documentLabel: newDocType.label,
                                documentTypeId: newDocType.id,
                            });
                        }}
                    />
                )}
                <Box sx={statusWrapperSx}>
                    {documentPreview && (
                        <DossierDocumentsValidationDocumentStatus
                            statusId={documentPreview?.statusId as number}
                            onAcceptDocument={handleAccept}
                            onDenyDocument={handleDeny}
                        />
                    )}
                    {documentPreview?.fichierId && documentPreview.fichierId && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: palette.primary.dark,
                                ':hover': {
                                    color: palette.primary.main,
                                },
                            }}
                            onClick={() =>
                                onDocumentDeliassage(documentPreview.fichierId as number)
                            }
                        >
                            <FaOptionIcon icon={['fasl', 'file-dashed-line']} />
                            Déliasser
                        </Button>
                    )}
                    {currentLayout !== LAYOUT_DOCUMENTS_FULL && (
                        <Button onClick={handleLayoutChange}>
                            <FaOptionIcon
                                sx={changeLayoutIconSx}
                                icon={['fasl', 'arrow-up-right-from-square']}
                            />
                        </Button>
                    )}
                </Box>
            </Box>
        );
    };

export default DossierDocumentsValidationHeader;
